import { Box, Stack, Card } from '@mui/material';
import { Typography, Button } from '@sweep-io/sweep-design';
import React, { useState, useCallback, useMemo } from 'react';
import { InfoDialog } from '../common/dialogs/InfoDialog';
import { SweepObjectSelector } from '../common/sweep-object-field-selectors/SweepObjectSelector';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';
import { useSelector } from 'react-redux';
import { Radio, colors } from '@sweep-io/sweep-design';
import { Cube, Metadata } from '@sweep-io/sweep-design/dist/icons';
import { MetadataTypeSelector } from './MetadataTypeSelector';
import { useInitializeMetadataTypes } from '../documentation/hooks/useInitializeMetadataTypes';

export const SELECTION_TYPES = {
  OBJECT: 'object',
  METADATA: 'metadata',
};

type SelectionType = (typeof SELECTION_TYPES)[keyof typeof SELECTION_TYPES];

interface CustomRadioCardProps {
  title: string;
  subtitle: string;
  Icon: JSX.Element;
  onClick: () => void;
  isSelected: boolean;
  selector: React.ReactNode;
}

const CustomRadioCard = ({
  title,
  subtitle,
  Icon,
  onClick,
  isSelected,
  selector,
}: CustomRadioCardProps) => {
  return (
    <Card
      onClick={onClick}
      sx={{
        boxShadow: 'none',
        borderRadius: '8px',
        position: 'relative',
        border: `1px solid ${colors.grey[300]}`,
        backgroundColor: colors.white,
        transition: 'none',
        cursor: 'pointer',
        height: '320px',
        '&:hover:not(.selected)': {
          borderColor: colors.grey[800],
        },
        ...(isSelected && {
          borderColor: colors.blue[500],
        }),
      }}
      className={isSelected ? 'selected' : ''}
    >
      <Stack p={3} gap={3}>
        <Box
          display="flex"
          alignSelf="flex-start"
          sx={{ '.MuiRadio-root': { padding: '2.5px 0' } }}
        >
          <Radio checked={isSelected} />
        </Box>
        <Stack gap={1}>
          {Icon}
          <Typography variant="h3">{title}</Typography>
          <Box sx={{ height: '96px' }}>
            <Typography variant="body" color={'text.secondary'}>
              {subtitle}
            </Typography>
          </Box>
        </Stack>
        {selector}
      </Stack>
    </Card>
  );
};

interface CreateMonitoringAgentDialogProps {
  closeDialog: () => void;
  onNext: (data: {
    selectionType: string;
    selectedObjectType?: string;
    selectedMetadataType?: string;
  }) => void;
}

const CreateMonitoringAgentDialog = ({ closeDialog, onNext }: CreateMonitoringAgentDialogProps) => {
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const [selectionType, setSelectionType] = useState<SelectionType>(SELECTION_TYPES.OBJECT);
  const [selectedObjectType, setSelectedObjectType] = useState<string>();
  const [selectedMetadataType, setSelectedMetadataType] = useState<string>();

  // Initialize metadata types for the selector
  useInitializeMetadataTypes();

  const handleMetadataSelect = useCallback((metadataType: string) => {
    setSelectedMetadataType(metadataType);
  }, []);

  const handleObjectSelect = useCallback((objectType: string) => {
    setSelectedObjectType(objectType);
  }, []);

  const handleNextClick = useCallback(() => {
    onNext({
      selectionType,
      selectedObjectType,
      selectedMetadataType,
    });
  }, [onNext, selectionType, selectedObjectType, selectedMetadataType]);

  const handleObjectTypeSelect = useCallback(() => {
    setSelectionType(SELECTION_TYPES.OBJECT);
  }, []);

  const handleMetadataTypeSelect = useCallback(() => {
    setSelectionType(SELECTION_TYPES.METADATA);
  }, []);

  const isFormValid = useMemo(
    () =>
      (selectionType === SELECTION_TYPES.OBJECT && selectedObjectType) ||
      (selectionType === SELECTION_TYPES.METADATA && selectedMetadataType),
    [selectionType, selectedObjectType, selectedMetadataType],
  );

  return (
    <>
      <InfoDialog
        open={true}
        titleJsx="Set up monitoring agent"
        titleTypographyVariant="h2"
        handleClose={closeDialog}
        PaperPropsSx={{ width: '760px', padding: 0 }}
        showCloseButton
        dialogActions={
          <Box>
            <Button onClick={handleNextClick} disabled={!isFormValid} size="medium">
              Next
            </Button>
          </Box>
        }
      >
        <Stack gap={3}>
          <Typography variant="body">How would you like to begin monitoring?</Typography>

          <Stack direction="row" gap={3}>
            <Box sx={{ flex: 1 }}>
              <CustomRadioCard
                title="Choose object"
                subtitle="Begin by selecting an object to monitor (account, contact, lead etc.) and find issues in its related configuration types."
                Icon={<Cube />}
                onClick={handleObjectTypeSelect}
                isSelected={selectionType === SELECTION_TYPES.OBJECT}
                selector={
                  <SweepObjectSelector
                    value={selectedObjectType}
                    crmOrgId={crmOrgId ?? ''}
                    onChange={handleObjectSelect}
                    fullWidth
                    placeholder="Choose an object"
                  />
                }
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <CustomRadioCard
                title="Choose metadata type"
                subtitle="Begin by selecting a metadata type (fields, automations, validation rules etc.) and find issues across that type."
                Icon={<Metadata />}
                onClick={handleMetadataTypeSelect}
                isSelected={selectionType === SELECTION_TYPES.METADATA}
                selector={
                  <MetadataTypeSelector
                    value={selectedMetadataType}
                    onChange={handleMetadataSelect}
                    crmOrgId={crmOrgId ?? ''}
                    fullWidth
                  />
                }
              />
            </Box>
          </Stack>
        </Stack>
      </InfoDialog>
    </>
  );
};

export default CreateMonitoringAgentDialog;
